import type { files as fileContracts } from '@personal-blog/contracts';
import { apiFetch } from '@/lib/api';
import { md5HexFromBlob } from './utils/md5.js';

const LIBRARY_UPLOAD_CHUNK_SIZE = 8 * 1024 * 1024;

export interface LibraryUploadTarget {
  partition: fileContracts.FilePartition;
  folderId: string | null;
  groupScopes?: readonly LibraryUploadGroupScope[];
  sameNameStrategy?: fileContracts.SameNameStrategy;
}

export type LibraryUploadGroupScope = fileContracts.GroupScopeInput & {
  groupSlug?: string | null | undefined;
  groupName?: string | null | undefined;
};

export interface LibraryUploadProgress {
  file: File;
  sentBytes: number;
  totalBytes: number;
}

export interface LibraryUploadFileOptions {
  signal?: AbortSignal;
  /** Existing upload session id to resume. When omitted a fresh init is created. */
  sessionId?: string;
  /** Emits the active server session as soon as it is known and after chunk writes. */
  onSession?: (session: fileContracts.UploadSession) => void;
}

export type LibraryUploadedFile = fileContracts.File & {
  /** True when init returned an existing library row instead of creating an upload session. */
  uploadReusedExisting?: boolean;
};

export async function readUploadSession(
  uploadId: string,
  signal?: AbortSignal,
): Promise<fileContracts.UploadSession> {
  const res = await apiFetch<fileContracts.UploadSessionResponse>(
    `/api/me/uploads/${encodeURIComponent(uploadId)}`,
    {
      ...(signal !== undefined ? { signal } : {}),
    },
  );
  return res.session;
}

export async function abortLibraryUpload(
  uploadId: string,
  signal?: AbortSignal,
): Promise<void> {
  await apiFetch<unknown>(`/api/me/uploads/${encodeURIComponent(uploadId)}`, {
    method: 'DELETE',
    ...(signal !== undefined ? { signal } : {}),
  });
}

export async function initLibraryUpload(
  file: File,
  target: LibraryUploadTarget,
  signal?: AbortSignal,
  expectedMd5?: string,
): Promise<fileContracts.UploadInitResponse> {
  return apiFetch<fileContracts.UploadInitResponse>(
    '/api/me/uploads/init',
    {
      method: 'POST',
      body: JSON.stringify({
        partition: target.partition,
        targetFolderId: target.folderId,
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        totalSizeBytes: file.size,
        chunkSize: LIBRARY_UPLOAD_CHUNK_SIZE,
        sameNameStrategy: target.sameNameStrategy ?? 'rename',
        ...(expectedMd5 !== undefined ? { expectedMd5 } : {}),
        ...(target.groupScopes !== undefined && target.groupScopes.length > 0
          ? { groupScopes: target.groupScopes.map(toApiGroupScope) }
          : {}),
      }),
      ...(signal !== undefined ? { signal } : {}),
    },
  );
}

function toApiGroupScope(scope: LibraryUploadGroupScope): fileContracts.GroupScopeInput {
  return {
    groupId: scope.groupId,
    ...(scope.availableAt !== undefined ? { availableAt: scope.availableAt } : {}),
    ...(scope.expiresAt !== undefined ? { expiresAt: scope.expiresAt } : {}),
    ...(scope.allowParentAccess !== undefined ? { allowParentAccess: scope.allowParentAccess } : {}),
    ...(scope.pendingOpen !== undefined ? { pendingOpen: scope.pendingOpen } : {}),
  };
}

export async function uploadLibraryFile(
  file: File,
  target: LibraryUploadTarget,
  onProgress?: (progress: LibraryUploadProgress) => void,
  options: LibraryUploadFileOptions = {},
): Promise<LibraryUploadedFile> {
  throwIfAborted(options.signal);
  const expectedMd5 =
    options.sessionId === undefined ? await md5HexFromBlob(file) : undefined;
  throwIfAborted(options.signal);
  let session: fileContracts.UploadSession;
  if (options.sessionId !== undefined) {
    session = await readUploadSession(options.sessionId, options.signal);
  } else {
    const init = await initLibraryUpload(file, target, options.signal, expectedMd5);
    if (init.kind === 'deduped') {
      onProgress?.({ file, sentBytes: file.size, totalBytes: file.size });
      return { ...init.existingFile, uploadReusedExisting: true };
    }
    session = init.session;
  }

  if (session.state === 'aborted') {
    throw new Error('Upload session was cancelled.');
  }
  if (session.state === 'completed') {
    throw new Error('Upload session was already completed.');
  }

  options.onSession?.(session);
  const chunkSize = session.chunkSize;
  const totalChunks = Math.ceil(file.size / chunkSize);
  const received = new Set(session.receivedChunks);
  onProgress?.({
    file,
    sentBytes: Math.min(file.size, Math.max(0, session.receivedBytes)),
    totalBytes: file.size,
  });

  for (let i = 0; i < totalChunks; i += 1) {
    throwIfAborted(options.signal);
    const start = i * chunkSize;
    const end = Math.min(file.size, start + chunkSize);
    if (received.has(i)) {
      onProgress?.({ file, sentBytes: end, totalBytes: file.size });
      continue;
    }
    const chunk = await apiFetch<{ session: fileContracts.UploadSession }>(
      `/api/me/uploads/${encodeURIComponent(session.id)}/chunks/${String(i)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file.slice(start, end),
        ...(options.signal !== undefined ? { signal: options.signal } : {}),
      },
    );
    for (const n of chunk.session.receivedChunks) received.add(n);
    options.onSession?.(chunk.session);
    onProgress?.({
      file,
      sentBytes: Math.min(file.size, Math.max(end, chunk.session.receivedBytes)),
      totalBytes: file.size,
    });
  }

  const completed = await apiFetch<fileContracts.UploadCompleteResponse>(
    `/api/me/uploads/${encodeURIComponent(session.id)}/complete`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...(expectedMd5 !== undefined ? { expectedMd5 } : {}),
      }),
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
    },
  );
  onProgress?.({ file, sentBytes: file.size, totalBytes: file.size });
  return { ...completed.file, uploadReusedExisting: false };
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted !== true) return;
  throw new DOMException('Upload cancelled.', 'AbortError');
}
